<template>
  <div class="tf">
    <span class="tf__info">Showing {{ from }}–{{ to }} of {{ total }} entries</span>

    <div class="tf__controls">
      <div class="tf__rows">
        <span>Rows per page:</span>
        <EcuSelect
          class="tf__select"
          size="sm"
          aria-label="Rows per page"
          :model-value="rowsPerPage"
          :options="rowsChoices"
          @update:model-value="onRows"
        />
      </div>

      <span class="tf__range">{{ from }}–{{ to }} of {{ total }}</span>

      <nav class="tf__pager" aria-label="Pagination">
        <button
          type="button"
          class="tf__btn"
          :disabled="page <= 1 || loading"
          aria-label="Previous page"
          @click="go(page - 1)"
        >
          <AppIcon name="chevron-left" :size="16" :stroke="2.2" />
        </button>

        <template v-for="item in pageItems" :key="item.key">
          <span v-if="item.gap" class="tf__gap">…</span>
          <button
            v-else
            type="button"
            class="tf__btn tf__btn--num"
            :class="{ 'is-active': item.n === page }"
            :aria-current="item.n === page ? 'page' : null"
            :disabled="loading"
            @click="go(item.n)"
          >
            {{ item.n }}
          </button>
        </template>

        <button
          type="button"
          class="tf__btn"
          :disabled="page >= pageCount || loading"
          aria-label="Next page"
          @click="go(page + 1)"
        >
          <AppIcon name="chevron-right" :size="16" :stroke="2.2" />
        </button>
      </nav>
    </div>
  </div>
</template>

<script>
import AppIcon from "./AppIcon.vue";
import EcuSelect from "./EcuSelect.vue";

// Footer for server-side tables (use with EasyDataTable's `hide-footer`).
// Controlled component: the parent owns page / rowsPerPage and reloads on change.
export default {
  name: "TableFooter",
  components: { AppIcon, EcuSelect },
  props: {
    page: { type: Number, default: 1 },
    rowsPerPage: { type: Number, default: 10 },
    total: { type: Number, default: 0 },
    rowsOptions: { type: Array, default: () => [10, 20, 50, 100] },
    loading: { type: Boolean, default: false },
  },
  emits: ["update:page", "update:rowsPerPage"],
  computed: {
    // the themed dropdown (orange selection) instead of the browser's native list
    rowsChoices() {
      return this.rowsOptions.map((n) => ({ value: n, label: String(n) }));
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.total / this.rowsPerPage));
    },
    from() {
      return this.total ? (this.page - 1) * this.rowsPerPage + 1 : 0;
    },
    to() {
      return Math.min(this.page * this.rowsPerPage, this.total);
    },
    // 1 … 4 5 6 … 9 style window
    pageItems() {
      const n = this.pageCount;
      const p = this.page;
      const num = (v) => ({ key: `p${v}`, n: v });
      if (n <= 7) return Array.from({ length: n }, (_, i) => num(i + 1));
      const items = [num(1)];
      const start = Math.max(2, p - 1);
      const end = Math.min(n - 1, p + 1);
      if (start > 2) items.push({ key: "gap-l", gap: true });
      for (let i = start; i <= end; i++) items.push(num(i));
      if (end < n - 1) items.push({ key: "gap-r", gap: true });
      items.push(num(n));
      return items;
    },
  },
  methods: {
    go(target) {
      const next = Math.min(Math.max(1, target), this.pageCount);
      if (next !== this.page) this.$emit("update:page", next);
    },
    onRows(value) {
      this.$emit("update:rowsPerPage", Number(value));
    },
  },
};
</script>

<style scoped>
.tf {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px 24px;
  padding-top: 16px;
  font-size: var(--ecu-fs-body);
  color: var(--ecu-ink, #1d2a4d);
}
.tf__controls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 22px;
}
.tf__rows {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  flex-wrap: nowrap;
  white-space: nowrap;
}
.tf__rows span {
  white-space: nowrap;
  flex-shrink: 0;
}
.tf__select {
  min-width: 74px;
}
.tf__range {
  white-space: nowrap;
}
.tf__pager {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.tf__btn {
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e3e6f0;
  border-radius: 10px;
  background: #fff;
  color: var(--ecu-ink, #1d2a4d);
  font: inherit;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease;
}
.tf__btn:hover:not(:disabled):not(.is-active) {
  border-color: var(--ecu-orange, #f97316);
  color: var(--ecu-orange-dark, #e84d00);
  background: #fff8f2;
}
.tf__btn:focus-visible {
  outline: 2px solid var(--ecu-orange, #f97316);
  outline-offset: 1px;
}
.tf__btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.tf__btn.is-active {
  border-color: transparent;
  color: #fff;
  background: linear-gradient(135deg, #ff8a2a, #f2610f);
  box-shadow: 0 6px 14px rgba(242, 97, 15, 0.28);
}
.tf__gap {
  width: 22px;
  text-align: center;
  color: var(--ecu-muted, #5f6579);
}
@media (max-width: 575px) {
  .tf { justify-content: center; text-align: center; }
  .tf__controls { justify-content: center; }
}
</style>